/* eslint-disable max-lines */
import {
  BROWSE_FILTER_MESSAGES,
  BROWSE_FILTER_ORDER,
  type ResolvedShortcut,
} from '@/components/ui/floating-ask-heidi/shortcut-marketplace-data';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerDescription,
  DialogDrawerTitle,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { SearchInput } from '@heidi/design-system/search-input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { TypographyP2, typographyVariant } from '@heidi/design-system/typography';
import { useIsMobile } from '@heidi/utils';
import { useCombinedQueriesTeamGeneralInfo } from '@heidi/workspace/data';
import { ChevronLeft, Plus, X } from 'lucide-react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { PinnedChip } from './evidence-settings-popover';
import { ShortcutBrowseItem } from './shortcut-browse-item';
import {
  SHORTCUT_DESCRIPTION_MAX_LENGTH,
  SHORTCUT_NAME_MAX_LENGTH,
  SHORTCUT_PROMPT_MAX_LENGTH,
} from './shortcut-constants';
import { ShortcutCreateView } from './shortcut-create-view';
import { shortcutMarketplaceModalMessages } from './shortcut-marketplace-modal.messages';
import { useShortcutBrowseState } from './use-shortcut-browse-state';

export interface ShortcutMarketplaceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pinnedShortcutIds: Set<string>;
  pinnedShortcutLabels: Set<string>;
  /**
   * Called when the user installs a shortcut. `origin` distinguishes
   * marketplace installs ('library') from user-authored shortcuts
   * ('custom') for analytics.
   */
  onAddShortcut: (shortcut: ResolvedShortcut, origin: 'library' | 'custom') => void;
  onRemoveShortcut?: (shortcut: ResolvedShortcut) => void;
  customShortcuts?: PinnedChip[];
  onEditShortcut?: (
    chipId: string,
    label: string,
    query: string,
    description?: string
  ) => void;
  onBack?: () => void;
  isUS?: boolean;
  isAtShortcutLimit?: boolean;
  shortcutCount?: number;
  maxShortcuts?: number;
}

export const ShortcutMarketplaceModal = memo(
  ({
    open,
    onOpenChange,
    pinnedShortcutIds,
    pinnedShortcutLabels,
    onAddShortcut,
    onRemoveShortcut,
    customShortcuts,
    onEditShortcut,
    onBack,
    isUS,
    isAtShortcutLimit = false,
    shortcutCount = 0,
    maxShortcuts = 25,
  }: ShortcutMarketplaceModalProps) => {
    const intl = useIntl();
    const isMobile = useIsMobile();
    const { isTeamAdmin, isTeamOwner } = useCombinedQueriesTeamGeneralInfo();
    const canManageTeamShortcuts = !!(isTeamAdmin || isTeamOwner);
    const [viewMode, setViewMode] = useState<'browse' | 'create'>('browse');
    const [createName, setCreateName] = useState('');
    const [createPrompt, setCreatePrompt] = useState('');
    const [createDescription, setCreateDescription] = useState('');
    const [editingShortcut, setEditingShortcut] = useState<PinnedChip | null>(null);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    // Team shortcuts are auto-pinned server-side and already flow through
    // `customShortcuts` / `pinnedShortcutIds`, so the marketplace no longer
    // needs a dedicated team-shortcut catalog fetch or "Browse & pin" flow.

    const {
      searchQuery,
      setSearchQuery,
      selectedFilter,
      setSelectedFilter,
      customShortcutIds,
      filteredShortcuts,
      isShortcutPinned,
      resetBrowseState,
    } = useShortcutBrowseState(
      customShortcuts,
      pinnedShortcutIds,
      pinnedShortcutLabels,
      isUS
    );

    useEffect(() => {
      if (!open) {
        setViewMode('browse');
        resetBrowseState();
        setCreateName('');
        setCreatePrompt('');
        setCreateDescription('');
        setEditingShortcut(null);
      }
      return () => {
        if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);
      };
    }, [open, resetBrowseState]);

    const handleSelect = useCallback(
      (shortcut: ResolvedShortcut) => {
        const isPinned = isShortcutPinned(shortcut);
        if (isPinned && onRemoveShortcut) onRemoveShortcut(shortcut);
        // User picked an existing marketplace shortcut → origin='library'.
        else if (!isPinned) onAddShortcut(shortcut, 'library');
      },
      [isShortcutPinned, onAddShortcut, onRemoveShortcut]
    );

    const handleCreateSave = useCallback(() => {
      if (!createName.trim() || !createPrompt.trim()) return;
      // User authored a new shortcut → origin='custom'.
      onAddShortcut(
        {
          id: `custom-${Date.now()}`,
          label: createName.trim(),
          query: createPrompt.trim(),
          category: 'plan',
          description:
            createDescription.trim() ||
            intl.formatMessage(shortcutMarketplaceModalMessages.customProtocol),
        },
        'custom'
      );
      onOpenChange(false);
    }, [createName, createPrompt, createDescription, intl, onAddShortcut, onOpenChange]);

    const handleEditSave = useCallback(() => {
      if (!editingShortcut || !createName.trim() || !createPrompt.trim()) return;
      onEditShortcut?.(
        editingShortcut.id,
        createName.trim(),
        createPrompt.trim(),
        createDescription.trim()
      );
      setEditingShortcut(null);
      onOpenChange(false);
    }, [
      editingShortcut,
      createName,
      createPrompt,
      createDescription,
      onEditShortcut,
      onOpenChange,
    ]);

    const canSave =
      createName.trim().length > 0 &&
      createPrompt.trim().length > 0 &&
      createName.length <= SHORTCUT_NAME_MAX_LENGTH &&
      createPrompt.length <= SHORTCUT_PROMPT_MAX_LENGTH &&
      createDescription.length <= SHORTCUT_DESCRIPTION_MAX_LENGTH;

    const switchToView = useCallback(
      (targetView: 'browse' | 'create') => {
        if (isTransitioning) return;
        setIsTransitioning(true);
        transitionTimeoutRef.current = setTimeout(() => {
          setViewMode(targetView);
          requestAnimationFrame(() =>
            requestAnimationFrame(() => setIsTransitioning(false))
          );
        }, 150);
      },
      [isTransitioning]
    );

    const handleEditClick = useCallback(
      (shortcut: ResolvedShortcut) => {
        const chip = (customShortcuts ?? []).find((c) => c.id === shortcut.id);
        if (!chip) return;
        setEditingShortcut(chip);
        setCreateName(chip.label);
        setCreatePrompt(chip.query);
        setCreateDescription(chip.description ?? '');
        switchToView('create');
      },
      [customShortcuts, switchToView]
    );

    const handleCancelEdit = useCallback(() => {
      setEditingShortcut(null);
      switchToView('browse');
    }, [switchToView]);

    const close = useCallback(() => onOpenChange(false), [onOpenChange]);

    return (
      <DialogDrawer open={open} onOpenChange={onOpenChange}>
        <DialogDrawerContent
          hideCloseIcon
          data-testid="evidence-shortcuts-marketplace"
          className="flex max-h-[85dvh] w-full flex-col gap-0 overflow-hidden bg-surface-page p-0 sm:h-150 sm:max-w-xl sm:rounded-xl"
        >
          <div className="mx-auto mt-4 h-2 w-24 shrink-0 rounded-full bg-background-secondary sm:hidden" />
          <div
            className={cn(
              'flex flex-1 flex-col overflow-hidden transition-[opacity,transform] duration-150 ease-out',
              isTransitioning ? 'translate-y-0.5 opacity-0' : 'translate-y-0 opacity-100'
            )}
          >
            {viewMode === 'browse' ? (
              <>
                <div className="flex shrink-0 items-center justify-between px-6 pt-6">
                  <div className="flex items-center gap-2">
                    {isMobile && onBack && (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconOnly
                        onClick={onBack}
                        aria-label={intl.formatMessage(
                          shortcutMarketplaceModalMessages.backAria
                        )}
                      >
                        <ChevronLeft className="rtl:-scale-x-100" />
                      </Button>
                    )}
                    <DialogDrawerTitle>
                      <FormattedMessage
                        {...shortcutMarketplaceModalMessages.browseShortcuts}
                      />
                    </DialogDrawerTitle>
                  </div>
                  <DialogDrawerDescription className="sr-only">
                    <FormattedMessage
                      {...shortcutMarketplaceModalMessages.browseShortcuts}
                    />
                  </DialogDrawerDescription>
                  <div className="flex items-center gap-1">
                    {isMobile && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex">
                            <Button
                              variant="default"
                              size="sm"
                              iconOnly
                              onClick={() => switchToView('create')}
                              disabled={isAtShortcutLimit}
                              data-testid="evidence-shortcuts-create-button"
                              aria-label={intl.formatMessage(
                                shortcutMarketplaceModalMessages.createShortcut
                              )}
                            >
                              <Plus />
                            </Button>
                          </span>
                        </TooltipTrigger>
                        {isAtShortcutLimit && (
                          <TooltipContent>
                            {intl.formatMessage(
                              shortcutMarketplaceModalMessages.savedShortcutLimitReached,
                              { max: maxShortcuts }
                            )}
                          </TooltipContent>
                        )}
                      </Tooltip>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      iconOnly
                      onClick={close}
                      aria-label={intl.formatMessage(
                        shortcutMarketplaceModalMessages.closeAria
                      )}
                    >
                      <X />
                    </Button>
                  </div>
                </div>

                <div className="shrink-0 px-6 pt-4">
                  <SearchInput
                    placeholder={intl.formatMessage(
                      shortcutMarketplaceModalMessages.searchShortcuts
                    )}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    clearAriaLabel={intl.formatMessage(
                      shortcutMarketplaceModalMessages.clearShortcutSearch
                    )}
                  />
                </div>
                <div className="flex shrink-0 gap-2 overflow-x-auto px-6 pb-2 pt-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>*]:shrink-0">
                  {BROWSE_FILTER_ORDER.map((filter) => (
                    <Button
                      key={filter}
                      variant={selectedFilter === filter ? 'default' : 'ghost'}
                      size="xs"
                      className={
                        selectedFilter !== filter ? 'bg-background-tertiary' : undefined
                      }
                      onClick={() => setSelectedFilter(filter)}
                      data-testid={`evidence-shortcuts-filter-${filter}`}
                    >
                      {intl.formatMessage(BROWSE_FILTER_MESSAGES[filter])}
                    </Button>
                  ))}
                </div>
                <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-6">
                  {filteredShortcuts.length === 0 ? (
                    <div className="flex items-center justify-center py-8">
                      <TypographyP2 className="text-text-tertiary">
                        <FormattedMessage
                          {...shortcutMarketplaceModalMessages.noShortcutsFound}
                        />
                      </TypographyP2>
                    </div>
                  ) : (
                    filteredShortcuts.map((shortcut) => (
                      <ShortcutBrowseItem
                        key={shortcut.id}
                        shortcut={shortcut}
                        isCustom={customShortcutIds.has(shortcut.id)}
                        isPinned={isShortcutPinned(shortcut)}
                        canRemove={!!onRemoveShortcut}
                        isAtShortcutLimit={isAtShortcutLimit}
                        maxShortcuts={maxShortcuts}
                        canManageTeamShortcuts={canManageTeamShortcuts}
                        onSelect={handleSelect}
                        onEdit={handleEditClick}
                        onRemove={onRemoveShortcut}
                      />
                    ))
                  )}
                </div>
                {isMobile && (
                  <div className="shrink-0 px-6 pb-4 pt-2 text-center">
                    <span
                      className={cn(
                        typographyVariant({ variant: 'caption' }),
                        'text-text-tertiary'
                      )}
                    >
                      {intl.formatMessage(
                        shortcutMarketplaceModalMessages.savedShortcutQuota,
                        { count: shortcutCount, max: maxShortcuts }
                      )}
                    </span>
                  </div>
                )}
                {!isMobile && (
                  <div className="flex shrink-0 items-center justify-between px-6 pb-6 pt-4">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex">
                          <Button
                            variant="default"
                            size="xs"
                            onClick={() => switchToView('create')}
                            disabled={isAtShortcutLimit}
                            data-testid="evidence-shortcuts-create-button"
                          >
                            <Plus />
                            <FormattedMessage
                              {...shortcutMarketplaceModalMessages.createShortcut}
                            />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {isAtShortcutLimit && (
                        <TooltipContent>
                          {intl.formatMessage(
                            shortcutMarketplaceModalMessages.savedShortcutLimitReached,
                            { max: maxShortcuts }
                          )}
                        </TooltipContent>
                      )}
                    </Tooltip>

                    <span
                      className={cn(
                        'shrink-0',
                        typographyVariant({ variant: 'caption' }),
                        'text-text-tertiary'
                      )}
                    >
                      {intl.formatMessage(
                        shortcutMarketplaceModalMessages.savedShortcutQuota,
                        { count: shortcutCount, max: maxShortcuts }
                      )}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <ShortcutCreateView
                createName={createName}
                onCreateNameChange={setCreateName}
                createPrompt={createPrompt}
                onCreatePromptChange={setCreatePrompt}
                createDescription={createDescription}
                onCreateDescriptionChange={setCreateDescription}
                canSave={canSave}
                onSave={editingShortcut ? handleEditSave : handleCreateSave}
                onCancel={handleCancelEdit}
                onClose={close}
                isEditing={!!editingShortcut}
              />
            )}
          </div>
        </DialogDrawerContent>
      </DialogDrawer>
    );
  }
);

ShortcutMarketplaceModal.displayName = 'ShortcutMarketplaceModal';
